package fr.manyverse.app.presentation.components.player_screen.inventory

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import fr.manyverse.app.domain.model.Character
import fr.manyverse.app.domain.model.InventoryItem
import fr.manyverse.app.domain.model.ItemCategory
import fr.manyverse.app.domain.model.TableMember
import fr.manyverse.app.presentation.components.ErrorState
import fr.manyverse.app.presentation.components.rememberImageSelection
import fr.manyverse.app.presentation.state.AsyncUiState
import fr.manyverse.app.presentation.viewmodel.InventoryViewModel
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.inventory_error_loading
import org.jetbrains.compose.resources.stringResource

@Composable
fun InventoryTab(
    viewModel: InventoryViewModel,
    /** Nom du personnage dont c'est l'inventaire, présenté comme l'expéditeur des objets donnés. */
    ownerName: String,
    tableMembers: List<TableMember>,
    characters: List<Character>,
    modifier: Modifier = Modifier
) {
    val itemsState by viewModel.itemsState.collectAsState()
    val createItemState by viewModel.createItemState.collectAsState()
    val giveItemState by viewModel.giveItemState.collectAsState()
    val pendingQuantities by viewModel.pendingQuantities.collectAsState()

    var selectedCategories by remember { mutableStateOf(ItemCategory.entries.toSet()) }
    var searchQuery by remember { mutableStateOf("") }
    var showAddDialog by remember { mutableStateOf(false) }
    val itemImage = rememberImageSelection()
    var showGiveDialog by remember { mutableStateOf(false) }
    var selectedItemForGive by remember { mutableStateOf<InventoryItem?>(null) }

    Column(modifier = modifier.fillMaxSize()) {
        InventoryTopBar(
            selectedCategories = selectedCategories,
            searchQuery = searchQuery,
            onCategoriesChanged = { selectedCategories = it },
            onSearchQueryChanged = { searchQuery = it },
            onAddClick = { showAddDialog = true }
        )

        when (val state = itemsState) {
            is AsyncUiState.Idle, is AsyncUiState.Loading -> {
                Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    CircularProgressIndicator()
                }
            }

            is AsyncUiState.Error -> {
                ErrorState(
                    message = state.message,
                    onRetry = viewModel::observeItems,
                    title = stringResource(Res.string.inventory_error_loading)
                )
            }

            is AsyncUiState.Success -> {
                InventoryItemList(
                    items = state.data,
                    selectedCategories = selectedCategories,
                    searchQuery = searchQuery,
                    pendingQuantities = pendingQuantities,
                    viewModel = viewModel,
                    onGiveClick = { item ->
                        selectedItemForGive = item
                        showGiveDialog = true
                    },
                    showGiveButton = true
                )
            }
        }
    }

    AddInventoryItemDialog(
        showDialog = showAddDialog,
        selectedImageData = itemImage.selected,
        onPickImage = itemImage::pick,
        onClearImage = { itemImage.reset() },
        onDismiss = {
            showAddDialog = false
            itemImage.reset()
            viewModel.resetCreateItemState()
        },
        onConfirm = { name, category, quantity ->
            viewModel.createItem(name, category, quantity, itemImage.selected)
        },
        isLoading = createItemState is AsyncUiState.Loading,
        errorMessage = (createItemState as? AsyncUiState.Error)?.message
    )

    // Close dialog on successful create
    if (createItemState is AsyncUiState.Success) {
        showAddDialog = false
        itemImage.reset()
        viewModel.resetCreateItemState()
    }

    selectedItemForGive?.let { item ->
        GiveInventoryItemDialog(
            showDialog = showGiveDialog,
            item = item,
            displayQuantity = pendingQuantities[item.id] ?: item.quantity,
            recipients = tableMembers,
            characters = characters,
            isLoading = giveItemState is AsyncUiState.Loading,
            errorMessage = (giveItemState as? AsyncUiState.Error)?.message,
            onDismiss = {
                showGiveDialog = false
                selectedItemForGive = null
                viewModel.resetGiveItemState()
            },
            onConfirm = { toCharacterId, quantity ->
                viewModel.giveItem(
                    item = item,
                    toCharacterId = toCharacterId,
                    quantity = quantity,
                    senderName = ownerName,
                    recipientUserId = tableMembers.firstOrNull { it.characterId == toCharacterId }?.userId,
                    recipientName = characters.firstOrNull { it.id == toCharacterId }?.name.orEmpty()
                )
            }
        )
    }

    // Close dialog on successful give
    if (giveItemState is AsyncUiState.Success) {
        showGiveDialog = false
        selectedItemForGive = null
        viewModel.resetGiveItemState()
    }
}
